import { Project } from "@/models/project.model"
import { useKnownBusinessEntitiesSaleItemsStore } from "@/store/known-business-entities-sale-items.store.ts"
import { SaleItem } from "@/models/sale-item.model"
import { computed, type Ref } from "vue"
import { useProjectsSaleItemsStore } from "@/store/projects-sale-items.store"
import { PurchaseItem } from "@/models/purchase-item.model"
import { useProjectsPurchaseItemsStore } from "@/store/projects-purchase-items.store"

export default function usePriceAgreements(
  project: Ref<Project | undefined>,
  administrativeEventItem: PurchaseItem | SaleItem
) {
  const projectsPurchaseItemsStore = useProjectsPurchaseItemsStore()
  const projectsSaleItemsStore = useProjectsSaleItemsStore()
  const knownBusinessEntitiesSaleItemStore = useKnownBusinessEntitiesSaleItemsStore()

  const unitPrice = computed(() => {
    if (administrativeEventItem instanceof PurchaseItem) {
      return getUnitPriceForPurchaseItem(administrativeEventItem)
    } else {
      return getUnitPriceForSaleItem(administrativeEventItem)
    }
  })

  function getUnitPriceForPurchaseItem(purchaseItem: PurchaseItem): string | undefined {
    if (project.value) {
      // Check if there is a project price agreement.
      const projectUnitPrice = projectsPurchaseItemsStore.getUnitPrice(project.value, purchaseItem)
      if (projectUnitPrice) {
        return projectUnitPrice
      }
    }

    // If no price agreement is found, return the unit price of the administrative event item.
    return purchaseItem.unitPrice
  }

  function getUnitPriceForSaleItem(saleItem: SaleItem): string | undefined {
    if (project.value) {
      // Primary check if there is a project price agreement.
      const projectUnitPrice = projectsSaleItemsStore.getUnitPrice(project.value, saleItem)
      if (projectUnitPrice) {
        return projectUnitPrice
      }

      // Secondary check if there is a known business entity price agreement.
      if (project.value.executor.knownBusinessEntity) {
        const knownBusinessEntityUnitPrice = knownBusinessEntitiesSaleItemStore.getUnitPrice(
          project.value.executor.knownBusinessEntity!,
          saleItem
        )
        if (knownBusinessEntityUnitPrice) {
          return knownBusinessEntityUnitPrice
        }
      }
    }

    // If no price agreement is found, return the unit price of the administrative event item.
    return saleItem.unitPrice
  }

  return {
    unitPrice
  }
}
